import { Footer } from '@/components/Footer';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[hsl(40,23%,96%)]">
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-16">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-[hsl(22,70%,44%)] transition-colors mb-8" data-testid="privacy-back-link">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <h1 className="text-3xl sm:text-4xl font-bold text-[hsl(120,10%,18%)] mb-2" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="privacy-heading">
          Privacy Policy
        </h1>
        <p className="text-sm text-stone-400 mb-10">Last updated: April 2025</p>

        <div className="space-y-8 text-stone-600 text-[15px] leading-relaxed">
          <section>
            <h2 className="text-lg font-semibold text-[hsl(120,10%,18%)] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>1. Introduction</h2>
            <p>
              Ayurveda Consult ("we", "us", or "our"), operated in association with AIAYS Institute, provides AI-powered Ayurvedic consultation services through this website. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform. By accessing or using our services, you agree to the terms outlined here.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[hsl(120,10%,18%)] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>2. Information We Collect</h2>
            <p className="mb-3">We collect the following categories of information:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Personal Information:</strong> Name, email address, phone number, age, and gender provided during account creation and the intake form.</li>
              <li><strong>Health Information:</strong> Health and lifestyle concerns, areas of concern, dietary habits, exercise frequency, digestion quality, energy levels, body type (dosha), and any detailed descriptions you provide during your consultation.</li>
              <li><strong>Payment Information:</strong> Payment details are processed securely by Stripe, our third-party payment processor. We do not store your credit card number or full payment details on our servers.</li>
              <li><strong>Authentication Data:</strong> If you sign in via Google, we receive your name, email address, and profile picture from Google. We do not receive or store your Google password.</li>
              <li><strong>Usage Data:</strong> Pages visited, time spent on pages, browser type, device information, and IP address collected automatically through cookies and analytics tools.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[hsl(120,10%,18%)] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>3. How We Use Your Information</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>To generate your personalised Ayurvedic assessment using AI technology.</li>
              <li>To facilitate doctor-reviewed assessments and one-on-one consultations where applicable.</li>
              <li>To process payments securely via Stripe.</li>
              <li>To send you your consultation report and related correspondence via email.</li>
              <li>To improve and optimise our services and user experience.</li>
              <li>To communicate with you regarding your account, support queries, or service updates.</li>
              <li>To comply with legal obligations and enforce our terms.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[hsl(120,10%,18%)] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>4. AI-Generated Assessments</h2>
            <p>
              Our platform uses artificial intelligence (OpenAI technology) to analyse the health and lifestyle information you provide and generate Ayurvedic assessments. Your data is sent to our AI provider solely for the purpose of generating your report. We do not use your health data to train AI models. The AI-generated content is for educational and informational purposes only and does not constitute medical advice.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[hsl(120,10%,18%)] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>5. Data Sharing and Third Parties</h2>
            <p className="mb-3">We share your information only with the following trusted parties and only as necessary:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Stripe:</strong> For secure payment processing.</li>
              <li><strong>Google:</strong> For authentication when you choose to sign in with Google.</li>
              <li><strong>OpenAI:</strong> For AI-powered assessment generation (health data only, no payment data).</li>
              <li><strong>Email Service Provider (Resend):</strong> To deliver consultation reports and notifications to your email.</li>
              <li><strong>Reviewing Practitioners:</strong> If you purchase a Doctor-Reviewed or 1-on-1 Consultation plan, your assessment data is shared with our certified Ayurvedic practitioners for review.</li>
            </ul>
            <p className="mt-3">We do not sell, rent, or trade your personal or health information to any third parties for marketing purposes.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[hsl(120,10%,18%)] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>6. Data Storage and Security</h2>
            <p>
              Your data is stored on secure, encrypted servers. We implement industry-standard security measures including HTTPS encryption, secure database access controls, and regular security audits. While we strive to protect your information, no method of electronic storage or transmission is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[hsl(120,10%,18%)] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>7. Cookies and Analytics</h2>
            <p>
              We use cookies and similar technologies (including Google Tag Manager) to enhance your browsing experience, analyse site traffic, and understand user behaviour. You can control cookie settings through your browser preferences. Disabling cookies may affect the functionality of certain features on our platform.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[hsl(120,10%,18%)] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>8. Your Rights</h2>
            <p className="mb-3">Depending on your jurisdiction, you may have the right to:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Access the personal data we hold about you.</li>
              <li>Request correction of inaccurate or incomplete data.</li>
              <li>Request deletion of your personal data.</li>
              <li>Object to or restrict certain processing of your data.</li>
              <li>Withdraw consent where processing is based on consent.</li>
            </ul>
            <p className="mt-3">
              To exercise any of these rights, please contact us at <a href="mailto:me@ayurvedaconsult.online" className="text-[hsl(22,70%,44%)] underline">me@ayurvedaconsult.online</a>.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[hsl(120,10%,18%)] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>9. Data Retention</h2>
            <p>
              We retain your consultation data for as long as your account is active or as needed to provide you ongoing access to your reports. If you request account deletion, we will remove your personal data within 30 days, except where retention is required by law.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[hsl(120,10%,18%)] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>10. Children's Privacy</h2>
            <p>
              Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you believe a child has provided us with personal data, please contact us and we will take steps to remove it.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[hsl(120,10%,18%)] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Any changes will be posted on this page with a revised "Last updated" date. Continued use of our services after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[hsl(120,10%,18%)] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>12. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or your personal data, contact us at:
            </p>
            <p className="mt-2">
              <strong>Ayurveda Consult / AIAYS Institute</strong><br />
              Email: <a href="mailto:me@ayurvedaconsult.online" className="text-[hsl(22,70%,44%)] underline">me@ayurvedaconsult.online</a><br />
              Website: <a href="https://aiays.org" target="_blank" rel="noopener noreferrer" className="text-[hsl(22,70%,44%)] underline">aiays.org</a>
            </p>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
